import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { saRadiosStorage, saStorageKPIs, type SARadioStorage } from '@/mocks/superAdminStorage';
import RadioStorageTable from './components/RadioStorageTable';
import RadioStorageDetailModal from './components/RadioStorageDetailModal';

export default function SuperAdminStorage() {
  const [radios] = useState<SARadioStorage[]>(saRadiosStorage);
  const [filterPlan, setFilterPlan] = useState('todas');
  const [detailRadio, setDetailRadio] = useState<SARadioStorage | null>(null);

  const kpis = saStorageKPIs;

  /* Filter counts */
  const planCounts = useMemo(() => {
    const data = saRadiosStorage;
    return {
      todas: data.length,
      free: data.filter((r) => r.plan === 'free').length,
      pro: data.filter((r) => r.plan === 'pro').length,
      premium: data.filter((r) => r.plan === 'premium').length,
    };
  }, []);

  /* Filter */
  const filteredRadios = useMemo(() => {
    let result = radios;
    if (filterPlan === 'free') result = result.filter((r) => r.plan === 'free');
    if (filterPlan === 'pro') result = result.filter((r) => r.plan === 'pro');
    if (filterPlan === 'premium') result = result.filter((r) => r.plan === 'premium');
    return result;
  }, [radios, filterPlan]);

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">RadioFrecuenciaWeb</span>
            </div>

            {/* Page header */}
            <div>
              <h2 className="text-foreground-950 font-bold text-lg">RadioFrecuenciaWeb</h2>
              <p className="text-foreground-400 text-xs mt-0.5">
                Almacenamiento global · {filteredRadios.length} de {radios.length} radios
              </p>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="bg-background-50 border border-background-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-hard-drive-3-line text-accent-600 text-base"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-[10px] font-medium">Storage utilizado</p>
                  <p className="text-foreground-800 text-lg font-bold">{kpis.totalUsedGB.toFixed(1)} GB</p>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center shrink-0">
                  <i className="ri-server-line text-foreground-500 text-base"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-[10px] font-medium">Storage disponible</p>
                  <p className="text-foreground-800 text-lg font-bold">{(kpis.totalCapacityGB - kpis.totalUsedGB).toFixed(0)} GB</p>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center shrink-0">
                  <i className="ri-movie-line text-rose-500 text-base"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-[10px] font-medium">Videos</p>
                  <p className="text-foreground-800 text-lg font-bold">{kpis.videosGB.toFixed(1)} GB</p>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                  <i className="ri-image-line text-amber-500 text-base"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-[10px] font-medium">Fotos</p>
                  <p className="text-foreground-800 text-lg font-bold">{kpis.fotosGB.toFixed(1)} GB</p>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <i className="ri-music-line text-emerald-500 text-base"></i>
                </div>
                <div>
                  <p className="text-foreground-400 text-[10px] font-medium">Audios</p>
                  <p className="text-foreground-800 text-lg font-bold">{kpis.audiosGB.toFixed(1)} GB</p>
                </div>
              </div>
            </div>

            {/* Plan filters */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              {[
                { key: 'todas', label: 'Todos', count: planCounts.todas },
                { key: 'free', label: 'Básico', count: planCounts.free },
                { key: 'pro', label: 'Pro', count: planCounts.pro },
                { key: 'premium', label: 'Premium', count: planCounts.premium },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => setFilterPlan(item.key)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    filterPlan === item.key
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 text-foreground-500 hover:text-foreground-700 border border-background-200 hover:bg-background-100'
                  }`}
                >
                  {item.label}
                  <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold ${
                    filterPlan === item.key
                      ? 'bg-background-50/20 text-background-50'
                      : 'bg-background-100 text-foreground-400'
                  }`}>
                    {item.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Radio table */}
            <RadioStorageTable
              radios={filteredRadios}
              filterPlan={filterPlan}
              onViewDetail={setDetailRadio}
              loading={false}
              error={undefined}
              onRetry={() => {}}
            />
          </div>
        </main>
      </div>

      {/* Detail modal */}
      <RadioStorageDetailModal radio={detailRadio} onClose={() => setDetailRadio(null)} />
    </div>
  );
}